import { useState, useCallback } from 'react';
import { message } from 'antd';
import { r5m3Api, WriteoffItem } from '../../../services/r5m3Api';

export interface ItemState {
  item: WriteoffItem;
  selected: boolean;
  validating: boolean;
  valid: boolean | null;   // null = not yet validated
  validationMsg?: string;
}

export function useR5M3() {
  const [items, setItems] = useState<ItemState[]>([]);
  const [loading, setLoading] = useState(false);
  const [executeLoading, setExecuteLoading] = useState(false);
  const [queried, setQueried] = useState(false);

  /** Query shipment items by range */
  const handleQuery = useCallback(async (startNo: string, endNo: string) => {
    if (!startNo.trim() && !endNo.trim()) {
      message.warning('請輸入查詢範圍');
      return;
    }
    setLoading(true);
    setQueried(false);
    try {
      const res = await r5m3Api.query(startNo.trim(), endNo.trim());
      const mapped: ItemState[] = (res.items || []).map((item) => ({
        item,
        selected: false,
        validating: false,
        valid: null,
      }));
      setItems(mapped);
      setQueried(true);
      if (mapped.length === 0) {
        message.info('查無符合條件的庫存交運單');
      } else {
        message.success(`查詢到 ${mapped.length} 筆資料`);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      message.error(err.response?.data?.error || '查詢失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  /** Toggle checkbox for a single item, validate on check */
  const handleToggleItem = useCallback(async (shipmentNo: string, checked: boolean) => {
    if (!checked) {
      setItems((prev) =>
        prev.map((s) =>
          s.item.fm130_01 === shipmentNo
            ? { ...s, selected: false }
            : s
        )
      );
      return;
    }

    // Check: validate first
    setItems((prev) =>
      prev.map((s) =>
        s.item.fm130_01 === shipmentNo
          ? { ...s, validating: true }
          : s
      )
    );

    try {
      const res = await r5m3Api.validateItem(shipmentNo);
      setItems((prev) =>
        prev.map((s) =>
          s.item.fm130_01 === shipmentNo
            ? {
                ...s,
                validating: false,
                selected: res.valid,
                valid: res.valid,
                validationMsg: res.message,
              }
            : s
        )
      );
      if (!res.valid) {
        message.warning(res.message || `單號 ${shipmentNo} 驗證未通過`);
      }
    } catch {
      setItems((prev) =>
        prev.map((s) =>
          s.item.fm130_01 === shipmentNo
            ? { ...s, validating: false, selected: false, valid: false, validationMsg: '驗證失敗' }
            : s
        )
      );
      message.error(`單號 ${shipmentNo} 驗證失敗`);
    }
  }, []);

  /** Execute writeoff for all selected items */
  const handleExecute = useCallback(async (): Promise<boolean> => {
    const selectedNos = items
      .filter((s) => s.selected)
      .map((s) => s.item.fm130_01);

    if (selectedNos.length === 0) {
      message.warning('請先勾選要沖銷的交運單');
      return false;
    }

    setExecuteLoading(true);
    try {
      const res = await r5m3Api.execute(selectedNos);
      if (res.failed === 0) {
        message.success(res.message || `沖銷完成，成功 ${res.success} 筆`);
      } else {
        message.warning(
          res.message || `成功 ${res.success} 筆，失敗 ${res.failed} 筆`
        );
        if (res.errors && res.errors.length > 0) {
          res.errors.forEach((err) => message.error(err));
        }
      }
      // Clear items after execution
      setItems([]);
      setQueried(false);
      return true;
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      message.error(err.response?.data?.error || '沖銷執行失敗');
      return false;
    } finally {
      setExecuteLoading(false);
    }
  }, [items]);

  const selectedCount = items.filter((s) => s.selected).length;

  return {
    items,
    loading,
    executeLoading,
    queried,
    selectedCount,
    handleQuery,
    handleToggleItem,
    handleExecute,
  };
}
